import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import DashboardShell from "../components/common/DashboardShell";
import { LeagueApi } from "../utils/leagueApi";

// One submission's scoring row: a number input per rubric category + submit.
function SubmissionJudgeCard({ submission, rubric, onSubmitted }) {
  const initial = {};
  rubric.forEach((cat) => {
    initial[cat.id] = submission.my_scores[cat.id] ?? "";
  });
  const [scores, setScores] = useState(initial);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const payload = {};
    Object.entries(scores).forEach(([catId, value]) => {
      if (value !== "") payload[catId] = Number(value);
    });
    await LeagueApi.submitEvaluation(submission.id, payload).catch(
      () => undefined,
    );
    setBusy(false);
    onSubmitted();
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <span className="font-semibold text-gray-800 dark:text-gray-100">
          {submission.team_name}
        </span>
        <span className="rounded-full bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:text-blue-300">
          avg {submission.score}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {rubric.map((cat) => (
          <label
            key={cat.id}
            className="flex items-center justify-between gap-2 text-sm text-gray-600 dark:text-gray-400"
            htmlFor={`s-${submission.id}-${cat.id}`}
          >
            <span>
              {cat.name}{" "}
              <span className="text-gray-400 dark:text-gray-500">
                (x{cat.weight}, /{cat.max_score})
              </span>
            </span>
            <input
              className="w-20 rounded border px-2 py-1 text-sm"
              id={`s-${submission.id}-${cat.id}`}
              max={cat.max_score}
              min={0}
              type="number"
              value={scores[cat.id]}
              onChange={(e) =>
                setScores({ ...scores, [cat.id]: e.target.value })
              }
            />
          </label>
        ))}
      </div>
      <button
        className="mt-3 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        disabled={busy}
        type="button"
        onClick={submit}
      >
        Save scores
      </button>
    </div>
  );
}

SubmissionJudgeCard.propTypes = {
  submission: PropTypes.object.isRequired,
  rubric: PropTypes.array.isRequired,
  onSubmitted: PropTypes.func.isRequired,
};

export default function JudgingBoard() {
  const [hackathons, setHackathons] = useState([]);
  const [selected, setSelected] = useState("");
  const [board, setBoard] = useState(null);
  const [placements, setPlacements] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    LeagueApi.judgingHackathons()
      .then((data) => setHackathons(data.hackathons || []))
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  const loadBoard = (hackathonId) => {
    setSelected(hackathonId);
    setPlacements(null);
    setBoard(null);
    if (!hackathonId) return;
    LeagueApi.judgingBoard(hackathonId)
      .then(setBoard)
      .catch(() => undefined);
  };

  const reloadBoard = () => {
    if (selected)
      LeagueApi.judgingBoard(selected)
        .then(setBoard)
        .catch(() => undefined);
  };

  const finalize = async () => {
    const res = await LeagueApi.finalizeJudging(selected).catch(
      () => undefined,
    );
    if (res) setPlacements(res.placements);
    reloadBoard();
  };

  return (
    <DashboardShell>
      <div className="mx-auto max-w-3xl space-y-6 p-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Judging
        </h1>

        {loading ? (
          <p className="py-8 text-center text-gray-400 dark:text-gray-500">
            Loading…
          </p>
        ) : (
          <>
            <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <p className="mb-2 font-semibold text-gray-700 dark:text-gray-300">
                Hackathon to judge
              </p>
              {hackathons.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  No hackathons have submissions to judge yet.
                </p>
              ) : (
                <select
                  aria-label="Hackathon to judge"
                  className="w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                  value={selected}
                  onChange={(e) => loadBoard(e.target.value)}
                >
                  <option value="">Select a hackathon…</option>
                  {hackathons.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name} — {h.chapter} ({h.status})
                    </option>
                  ))}
                </select>
              )}
            </section>

            {board ? (
              <>
                <section className="space-y-3">
                  {board.submissions.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      No submissions to judge.
                    </p>
                  ) : (
                    board.submissions.map((submission) => (
                      <SubmissionJudgeCard
                        key={submission.id}
                        rubric={board.rubric}
                        submission={submission}
                        onSubmitted={reloadBoard}
                      />
                    ))
                  )}
                </section>

                {board.submissions.length > 0 ? (
                  <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
                    <button
                      className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                      type="button"
                      onClick={finalize}
                    >
                      Finalize judging &amp; award points
                    </button>
                    {placements ? (
                      <p className="mt-3 text-sm text-green-700 dark:text-green-300">
                        Podium finalized — points awarded to{" "}
                        {Object.keys(placements).length} team(s).
                      </p>
                    ) : null}
                  </section>
                ) : null}
              </>
            ) : null}
          </>
        )}
      </div>
    </DashboardShell>
  );
}
